import { z } from 'zod';
import { email, id, isoDate, listInput, nonEmpty, optionalText, url } from './common';
import { consentBasis } from './crm';
import { callingWindowSchema } from './workspace';

// ---------------------------------------------------------------------------
// Prospecting
// ---------------------------------------------------------------------------

export const PROSPECT_KINDS = ['business', 'person'] as const;
export const prospectKind = z.enum(PROSPECT_KINDS);
export type ProspectKind = z.infer<typeof prospectKind>;

export const PROSPECT_PROVIDERS = ['stub', 'google_places', 'scout'] as const;
export const prospectProviderId = z.enum(PROSPECT_PROVIDERS);
export type ProspectProviderId = z.infer<typeof prospectProviderId>;

export const PROSPECT_STATUSES = ['new', 'imported', 'dismissed'] as const;
export const prospectStatus = z.enum(PROSPECT_STATUSES);
export type ProspectStatus = z.infer<typeof prospectStatus>;

export const SEARCH_STATUSES = ['pending', 'completed', 'failed'] as const;
export const searchStatus = z.enum(SEARCH_STATUSES);
export type SearchStatus = z.infer<typeof searchStatus>;

export const prospectQuerySchema = z.object({
  kind: prospectKind.default('business'),
  industry: z.string().trim().max(80).optional(),
  keywords: z.array(z.string().trim().min(1).max(60)).max(10).default([]),
  city: z.string().trim().max(80).optional(),
  region: z.string().trim().toUpperCase().max(2).optional(),
  country: z.enum(['CA', 'US']).default('CA'),
  /** Job titles (person searches). */
  titles: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
  limit: z.number().int().min(1).max(100).default(25),
});
export type ProspectQuery = z.infer<typeof prospectQuerySchema>;

export const createSearchInput = z.object({
  query: prospectQuerySchema,
  /** Save results into this prospect list (created when `newListName` is given). */
  listId: id.optional(),
  newListName: z.string().trim().max(120).optional(),
});
export type CreateSearchInput = z.infer<typeof createSearchInput>;

export const listProspectsInput = listInput.extend({
  listId: id.optional(),
  searchId: id.optional(),
  status: prospectStatus.optional(),
  minScore: z.number().int().min(0).max(100).optional(),
});

export const importProspectsInput = z.object({
  prospectIds: z.array(id).min(1).max(200),
  /** Static contact list to add the new contacts to (e.g. a campaign's audience). */
  contactListId: id.optional(),
  createDeals: z.boolean().default(false),
  /** Legal basis recorded on imported contacts. Business listings default to b2b_business_line. */
  consentBasis: consentBasis.optional(),
});
export type ImportProspectsInput = z.infer<typeof importProspectsInput>;

// ---------------------------------------------------------------------------
// Outbound calling
// ---------------------------------------------------------------------------

export const CAMPAIGN_STATUSES = ['draft', 'active', 'paused', 'completed', 'archived'] as const;
export const campaignStatus = z.enum(CAMPAIGN_STATUSES);
export type CampaignStatus = z.infer<typeof campaignStatus>;

export const CALL_MODES = ['ai', 'human'] as const;
export const callMode = z.enum(CALL_MODES);
export type CallMode = z.infer<typeof callMode>;

/** Outcomes of a call, logged on the CRM timeline and used to move deals. */
export const CALL_OUTCOMES = [
  'connected',
  'voicemail',
  'no_answer',
  'busy',
  'interested',
  'meeting_booked',
  'not_interested',
  'do_not_call',
  'callback_requested',
  'failed',
] as const;
export const callOutcome = z.enum(CALL_OUTCOMES);
export type CallOutcome = z.infer<typeof callOutcome>;

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  connected: 'Connected',
  voicemail: 'Voicemail',
  no_answer: 'No answer',
  busy: 'Busy',
  interested: 'Interested',
  meeting_booked: 'Meeting booked',
  not_interested: 'Not interested',
  do_not_call: 'Do not call',
  callback_requested: 'Callback requested',
  failed: 'Failed',
};

export const CALL_STATUSES = ['initiated', 'ringing', 'in_progress', 'completed', 'failed', 'blocked'] as const;
export const callStatus = z.enum(CALL_STATUSES);
export type CallStatus = z.infer<typeof callStatus>;

export const TARGET_STATUSES = ['queued', 'dialing', 'completed', 'skipped', 'dnc', 'failed'] as const;
export const targetStatus = z.enum(TARGET_STATUSES);
export type TargetStatus = z.infer<typeof targetStatus>;

export const VOICE_PROVIDERS = ['stub', 'elevenlabs', 'twilio', 'manual'] as const;
export const voiceProviderId = z.enum(VOICE_PROVIDERS);
export type VoiceProviderId = z.infer<typeof voiceProviderId>;

export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

export const campaignScheduleSchema = z.object({
  /** 0 = Sunday ... 6 = Saturday, in the callee's local time. */
  days: z.array(z.number().int().min(0).max(6)).min(1).max(7).default([1, 2, 3, 4, 5]),
  /** Campaign window in the callee's local time (intersected with the legal window). */
  window: callingWindowSchema.default({ start: '09:30', end: '17:00' }),
  startsAt: isoDate.optional().nullable(),
  endsAt: isoDate.optional().nullable(),
});
export type CampaignSchedule = z.infer<typeof campaignScheduleSchema>;

export const createCampaignInput = z.object({
  name: nonEmpty(120),
  goal: optionalText(1000),
  /** What the AI agent says and aims for after the disclosure. */
  script: z.string().trim().max(10_000).default(''),
  mode: callMode.default('ai'),
  contactListId: id.optional().nullable(),
  pipelineId: id.optional().nullable(),
  schedule: campaignScheduleSchema.default(campaignScheduleSchema.parse({})),
  maxAttempts: z.number().int().min(1).max(10).default(3),
  retryDelayMinutes: z.number().int().min(15).max(7 * 24 * 60).default(24 * 60),
  /** Auto-dialer pacing: calls started per minute and simultaneous calls. */
  callsPerMinute: z.number().int().min(1).max(30).default(2),
  maxConcurrent: z.number().int().min(1).max(10).default(1),
  autoDial: z.boolean().default(false),
  voiceAgentId: z.string().trim().max(120).optional().nullable(),
});
export type CreateCampaignInput = z.infer<typeof createCampaignInput>;

export const updateCampaignInput = createCampaignInput.partial().extend({ id });
export type UpdateCampaignInput = z.infer<typeof updateCampaignInput>;

export const setCampaignStatusInput = z.object({ id, status: campaignStatus });

export const listCallsInput = listInput.extend({
  campaignId: id.optional(),
  contactId: id.optional(),
  outcome: callOutcome.optional(),
  status: callStatus.optional(),
});

export const startManualCallInput = z.object({ contactId: id, dealId: id.optional().nullable(), campaignId: id.optional().nullable() });
export const completeManualCallInput = z.object({
  callId: id,
  outcome: callOutcome,
  durationSeconds: z.number().int().min(0).max(6 * 3600).default(0),
  notes: optionalText(10_000),
});
export const setCallOutcomeInput = z.object({ callId: id, outcome: callOutcome, notes: optionalText(5000) });

// ---------------------------------------------------------------------------
// Compliance
// ---------------------------------------------------------------------------

export const DNC_SOURCES = ['manual', 'opt_out', 'import', 'call_outcome', 'sms_stop', 'email_unsubscribe', 'api'] as const;
export const dncSource = z.enum(DNC_SOURCES);
export type DncSource = z.infer<typeof dncSource>;

export const addDncInput = z.object({
  phone: z.string().trim().min(7).max(40),
  reason: optionalText(500),
  source: dncSource.default('manual'),
});
export const importDncInput = z.object({ csv: z.string().min(1).max(1_000_000), reason: optionalText(500) });
export const checkCallInput = z.object({ contactId: id, mode: z.enum(['ai', 'human']).default('ai'), campaignId: id.optional() });

// ---------------------------------------------------------------------------
// Sequences (Growth)
// ---------------------------------------------------------------------------

export const SEQUENCE_CHANNELS = ['call', 'email', 'sms', 'task'] as const;
export const sequenceChannel = z.enum(SEQUENCE_CHANNELS);
export type SequenceChannel = z.infer<typeof sequenceChannel>;

export const SEQUENCE_STATUSES = ['draft', 'active', 'paused', 'archived'] as const;
export const sequenceStatus = z.enum(SEQUENCE_STATUSES);
export type SequenceStatus = z.infer<typeof sequenceStatus>;

export const ENROLLMENT_STATUSES = ['active', 'completed', 'stopped'] as const;
export const enrollmentStatus = z.enum(ENROLLMENT_STATUSES);
export type EnrollmentStatus = z.infer<typeof enrollmentStatus>;

export const STOP_REASONS = ['replied', 'meeting_booked', 'opted_out', 'manual', 'bounced', 'completed'] as const;
export const stopReason = z.enum(STOP_REASONS);
export type StopReason = z.infer<typeof stopReason>;

export const sequenceStepInput = z.object({
  channel: sequenceChannel,
  /** Wait after the previous step (or enrollment) before this one runs. */
  delayMinutes: z.number().int().min(0).max(60 * 24 * 60).default(0),
  subject: optionalText(255),
  body: z.string().trim().max(10_000).default(''),
});
export type SequenceStepInput = z.infer<typeof sequenceStepInput>;

export const createSequenceInput = z.object({
  name: nonEmpty(120),
  description: optionalText(1000),
  stopOnReply: z.boolean().default(true),
  stopOnBooked: z.boolean().default(true),
  stopOnOptOut: z.literal(true).default(true),
  steps: z.array(sequenceStepInput).min(1).max(20),
});
export type CreateSequenceInput = z.infer<typeof createSequenceInput>;
export const updateSequenceInput = createSequenceInput.partial().extend({ id, status: sequenceStatus.optional() });
export const enrollInput = z.object({ sequenceId: id, contactIds: z.array(id).min(1).max(500) });
export const recordReplyInput = z.object({ contactId: id, channel: z.enum(['email', 'sms', 'call']).default('email') });

// ---------------------------------------------------------------------------
// Integrations (Growth): CRM sync, API keys, webhooks
// ---------------------------------------------------------------------------

export const CRM_SYNC_PROVIDERS = ['hubspot', 'salesforce'] as const;
export const crmSyncProvider = z.enum(CRM_SYNC_PROVIDERS);
export type CrmSyncProvider = z.infer<typeof crmSyncProvider>;

export const connectCrmInput = z.object({ provider: crmSyncProvider, accessToken: z.string().trim().max(500).optional(), instanceUrl: url.optional() });

export const API_KEY_SCOPES = ['read', 'write', 'admin'] as const;
export const apiKeyScope = z.enum(API_KEY_SCOPES);
export type ApiKeyScope = z.infer<typeof apiKeyScope>;
export const createApiKeyInput = z.object({ name: nonEmpty(120), scopes: z.array(apiKeyScope).min(1).default(['read']) });

export const WEBHOOK_EVENTS = [
  'contact.created',
  'contact.updated',
  'deal.created',
  'deal.stage_changed',
  'call.completed',
  'prospect.imported',
  'dnc.added',
  'sequence.stopped',
] as const;
export const webhookEvent = z.enum(WEBHOOK_EVENTS);
export type WebhookEvent = z.infer<typeof webhookEvent>;
export const createWebhookInput = z.object({ url, events: z.array(webhookEvent).min(1), description: optionalText(255) });

// ---------------------------------------------------------------------------
// Billing (standalone) and onboarding
// ---------------------------------------------------------------------------

export const checkoutInput = z.object({ plan: z.enum(['sales_starter', 'sales_growth']) });
export const contactUsInput = z.object({ email, message: z.string().max(2000) });
